import {
  asId,
  type AreaId,
  type EmergencyContactId,
  type HolidayId,
  type IncapacityId,
  type PermissionRequestId,
  type PersonId,
  type RequestId,
  type SubAreaId,
} from '@shared/brand';
import { err, ok, type Result } from '@shared/result';
import type {
  BalanceAdjusted,
  BalanceAdjustment,
  CreatedPerson,
  CreatePersonInput,
  TerminatePersonInput,
  TerminationResult,
  ReactivatePersonInput,
  ReactivationResult,
  PersonDetailPatch,
  PersonDetailUpdate,
  AuditScope,
  CreateEmergencyContactInput,
  IAuditLogRepository,
  IAuthGateway,
  BalanceRef,
  IBalanceRepository,
  IEmergencyContactRepository,
  IHolidayRepository,
  IIncapacityRepository,
  IOrganizationRepository,
  IPermissionRequestRepository,
  IPersonLeaderRepository,
  IPersonRepository,
  ISalaryRepository,
  SetSalaryInput,
  IRuntimeConfigRepository,
  IVacationRequestRepository,
  IncapacityScope,
  Page,
  PageRequest,
  PermissionScope,
  PersonScope,
  RegisterIncapacityInput,
  RepositoryError,
  RosterImportResult,
  RequestScope,
  SubmitPermissionInput,
  AssignVacationInput,
  AssignVacationResult,
  IDataConfirmationRepository,
  IPersonalRecordRepository,
  SavePersonalRecordInput,
  AttachPersonDocumentInput,
  SetPersonPermissionsInput,
  SubmitDataConfirmationInput,
  SubmitRequestInput,
  TestMailReceipt,
  AddedPosition,
  UpdateEmergencyContactInput,
  AuthenticatedUser,
  SignInError,
  TransferAreaInput,
  TransferAreaResult,
  SaveAreaCatalogInput,
  ResolveOverdueInput,
  ResolveOverdueResult,
  IOrgChartRepository,
  SaveOrgChartInput,
  IBirthdayRepository,
  IWhatsAppLinkRepository,
  IAssistantSettingsRepository,
  MergePendingAccountInput,
  PendingAccountMatch,
} from '@application/ports';
import type { Birthday } from '@domain/entities/Birthday';
import type { WhatsAppLinkCode, WhatsAppLinkStatus } from '@domain/entities/WhatsAppLink';
import type {
  AssistantKeyStatus,
  AssistantModelTest,
  AssistantProvider,
  AssistantSettings,
  ModelProvider,
} from '@domain/entities/AssistantSettings';
import type { OrgChart } from '@domain/entities/OrgChart';
import { ORG_CHART_MAX_NODES } from '@domain/entities/OrgChart';
import { personCan } from '@domain/policies/RbacPolicy';
import { decodeOrgChart } from '../codecs/orgChart';
import { CalendarDate } from '@domain/value-objects/CalendarDate';
import type { PersonalRecord } from '@domain/entities/PersonalRecord';
import {
  isBloodType,
  isEducationLevel,
  isMaritalStatus,
} from '@domain/entities/PersonalRecord';
import { DateRange } from '@domain/value-objects/DateRange';
import type { AuditEntry } from '@domain/entities/AuditEntry';
import type { Balance } from '@domain/entities/Balance';
import type { Holiday } from '@domain/entities/Holiday';
import { enabledHolidayDates } from '@domain/entities/Holiday';
import { countBusinessDays } from '@domain/policies/BusinessDayPolicy';
import { completedYearsOfService } from '@domain/policies/TenurePolicy';
import {
  cleanPositionTitle,
  findPositionByTitle,
  isPositionTitleValid,
  positionKey,
} from '@domain/policies/PositionCatalog';
import type { Incapacity } from '@domain/entities/Incapacity';
import { DEFAULT_INCAPACITY_KINDS } from '@domain/entities/Incapacity';
import type {
  ConfirmableField,
  DataConfirmation,
} from '@domain/entities/DataConfirmation';
import {
  CONFIRMATION_VERSION,
  cleanCorrections,
  isConfirmableField,
} from '@domain/entities/DataConfirmation';
import type { Area, Company, SubArea } from '@domain/entities/Organization';
import type { PermissionRequest } from '@domain/entities/PermissionRequest';
import { DEFAULT_PERMISSION_KINDS } from '@domain/entities/PermissionRequest';
import type { EmergencyContact, Person, PersonLeaderLink } from '@domain/entities/Person';
import { DEFAULT_NOTIFICATION_PREFERENCES } from '@domain/entities/Person';
import { PersonFullName } from '@domain/value-objects/PersonFullName';
import type { SalaryRecord } from '@domain/entities/Compensation';
import {
  DEFAULT_IDENTITY,
  DEFAULT_REJECTION_REASONS,
  DEFAULT_RULES,
  EMAIL_EVENTS,
  type EmailEvent,
  type EmailTemplate,
  type Position,
  type RuntimeConfig,
} from '@domain/entities/RuntimeConfig';
import type {
  RequestApproval,
  RequestWorkItem,
  VacationRequest,
} from '@domain/entities/VacationRequest';
import { canDeleteAssignedVacation } from '@domain/entities/VacationRequest';
import {
  DEFAULT_FEATURES,
  DEFAULT_ROLE_PERMISSIONS,
  isPermission,
} from '@domain/vocabulary';
import {
  DEMO_APPROVALS,
  DEMO_AREAS,
  DEMO_AUDIT,
  DEMO_ALL_PEOPLE,
  DEMO_BALANCES,
  DEMO_COMPANY,
  DEMO_CONTACTS,
  DEMO_HOLIDAYS,
  DEMO_INCAPACITIES,
  DEMO_LEADER_LINKS,
  DEMO_POSITIONS,
  DEMO_LEADER_PROJECTS,
  DEMO_PERMISSIONS,
  DEMO_REQUESTS,
  DEMO_SUB_AREAS,
  DEMO_WORK_ITEMS,
} from './seed';

/**
 * Demo adapters: the same ports, backed by arrays.
 *
 * This is the architecture paying for itself. Nothing in `domain/` or `application/`
 * changes to run without a backend — the composition root simply hands the use cases
 * a different set of implementations. If swapping the whole persistence layer needed
 * edits above this line, the dependency rule would not be real.
 *
 * Mutations here update the in-memory arrays so the UI behaves correctly within a
 * session (approve a request and the board moves), but nothing survives a reload.
 */

const clone = <T>(items: readonly T[]): T[] => [...items];

const paged = <T>(items: readonly T[], page: PageRequest): Page<T> => {
  const start = page.cursor === undefined ? 0 : Number(page.cursor.token);
  const slice = items.slice(start, start + page.limit);
  const nextIndex = start + page.limit;
  return {
    items: slice,
    nextCursor: nextIndex < items.length ? { token: String(nextIndex) } : null,
  };
};

const notFound = (what: string): RepositoryError => ({ kind: 'not-found', what });

/** Mutable state, shared by the adapters so a write is visible to every reader. */
class DemoStore {
  people = clone(DEMO_ALL_PEOPLE);
  links = clone(DEMO_LEADER_LINKS);
  requests = clone(DEMO_REQUESTS);
  approvals = clone(DEMO_APPROVALS);
  workItems = clone(DEMO_WORK_ITEMS);
  permissions = clone(DEMO_PERMISSIONS);
  incapacities = clone(DEMO_INCAPACITIES);
  balances = clone(DEMO_BALANCES);
  contacts = clone(DEMO_CONTACTS);
  holidays = clone(DEMO_HOLIDAYS);
  auditEntries = clone(DEMO_AUDIT);
  subAreas = clone(DEMO_SUB_AREAS);
  areas = clone(DEMO_AREAS);
  /*
   * Los sueldos, en su propio mapa. Vacío al arrancar a propósito: el caso
   * «todavía no tiene salario registrado» es el que hay que poder ver, y sembrar
   * uno lo escondería.
   */
  salaries = new Map<PersonId, SalaryRecord>();
  personalRecords = new Map<PersonId, PersonalRecord>();
  dataConfirmations = new Map<PersonId, DataConfirmation>();

  /**
   * Who is acting right now.
   *
   * A real backend reads this from the auth token, which is why the write ports
   * do not carry a `personId` — accepting one from the client would let a
   * request be filed on someone else's behalf. The demo has to get it from
   * somewhere equivalent, so the auth gateway publishes it here.
   */
  actingPersonId: PersonId | null = null;

  /*
   * The runtime configuration lives here rather than inside the config adapter
   * so the write adapters can read the *live* catalogue — a permit filed after
   * RR.HH. edits a kind must copy the edited values, not the seeded ones.
   *
   * Built on first read: `initialConfig` is defined below this class, and by
   * the time anything asks for the configuration the module has finished
   * evaluating.
   */
  private seededConfig: RuntimeConfig | null = null;

  get config(): RuntimeConfig {
    this.seededConfig ??= initialConfig();
    return this.seededConfig;
  }

  set config(next: RuntimeConfig) {
    this.seededConfig = next;
  }

  /** The acting person's own record, with their area and sub area attached. */
  actor(): Person | undefined {
    if (this.actingPersonId === null) return undefined;
    return this.people.find((p) => p.id === this.actingPersonId);
  }

  /** Next folio in a series, continuing from the highest already seeded. */
  nextFolio(prefix: string, existing: readonly { folio: string }[], pad: number): string {
    const highest = existing.reduce((max, item) => {
      const digits = /(\d+)$/.exec(item.folio);
      const value = digits === null ? 0 : Number(digits[1]);
      return value > max ? value : max;
    }, 0);
    return `${prefix}-${String(highest + 1).padStart(pad, '0')}`;
  }
}

export const demoStore = new DemoStore();

const auditStamp = (): { createdAt: Date; updatedAt: Date; schemaVersion: number } => {
  const now = new Date();
  return { createdAt: now, updatedAt: now, schemaVersion: 1 };
};

/** Display name for a sub area id, blank when the person belongs to none. */
const subAreaNameOf = (subAreaId: SubAreaId | null): string =>
  subAreaId === null ? '' : (demoStore.subAreas.find((s) => s.id === subAreaId)?.name ?? '');

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

/**
 * A signed-in session without Google.
 *
 * Demo mode has to start *already* authenticated, because there is no identity
 * provider to talk to. The listener fires once with the chosen person.
 */
export class DemoAuthGateway implements IAuthGateway {
  private current: AuthenticatedUser | null = null;
  private readonly listeners = new Set<(user: AuthenticatedUser | null) => void>();

  constructor(private personId: PersonId) {
    demoStore.actingPersonId = personId;
    this.current = this.buildUser(personId);
  }

  /** Switches the acting person, so every role can be reviewed without a backend. */
  actAs(personId: PersonId): void {
    this.personId = personId;
    // The write adapters read this instead of trusting a client-supplied id,
    // mirroring how the real backend derives it from the token.
    demoStore.actingPersonId = personId;
    this.current = this.buildUser(personId);
    for (const listener of this.listeners) listener(this.current);
  }

  private buildUser(personId: PersonId): AuthenticatedUser | null {
    const person = demoStore.people.find((p) => p.id === personId);
    if (person === undefined) return null;

    return {
      uid: person.uid ?? `uid-${person.id}`,
      email: person.email,
      displayName: person.displayName,
      photoURL: person.photoURL,
      emailVerified: true,
      claims: {
        role: person.role,
        areaId: person.areaId,
        personId: person.id,
        status: person.status === 'Desconocido' ? 'pending' : person.status,
      },
    };
  }

  signInWithGoogle(): Promise<Result<AuthenticatedUser, SignInError>> {
    const user = this.buildUser(this.personId);
    if (user === null) {
      return Promise.resolve(err({ kind: 'unknown', detail: 'Persona demo no encontrada.' }));
    }
    this.current = user;
    for (const listener of this.listeners) listener(user);
    return Promise.resolve(ok(user));
  }

  /** The demo has no passwords; any credentials land on the same fake session. */
  signInWithPassword(): Promise<Result<AuthenticatedUser, SignInError>> {
    return this.signInWithGoogle();
  }

  /*
   * En demo se concede sin preguntar, y se dice por qué: no hay Firebase contra
   * el que reidentificarse. Fingir una ventana de Google haría creer que el paso
   * está probado cuando lo único probado sería el diálogo.
   */
  reauthenticate(): Promise<boolean> {
    return Promise.resolve(true);
  }

  signOut(): Promise<void> {
    this.current = null;
    for (const listener of this.listeners) listener(null);
    return Promise.resolve();
  }

  currentUser(): AuthenticatedUser | null {
    return this.current;
  }

  onAuthStateChanged(listener: (user: AuthenticatedUser | null) => void): () => void {
    this.listeners.add(listener);
    // Emitted asynchronously so subscribers mount before the first notification,
    // matching how the Firebase listener behaves.
    queueMicrotask(() => {
      listener(this.current);
    });
    return () => this.listeners.delete(listener);
  }

  refreshClaims(): Promise<Result<AuthenticatedUser, SignInError>> {
    const user = this.buildUser(this.personId);
    return Promise.resolve(
      user === null ? err({ kind: 'unknown', detail: 'Sin sesión.' }) : ok(user),
    );
  }
}

// ---------------------------------------------------------------------------
// Runtime configuration
// ---------------------------------------------------------------------------

/**
 * The eight notices, copied from the prototype's `MAILS`.
 *
 * Real subjects rather than `Aviso · ${event}`: this screen exists so someone
 * can judge whether the wording is right before it reaches 500 inboxes, and a
 * placeholder makes that impossible to judge.
 */
const TEMPLATES: Readonly<Record<EmailEvent, Omit<EmailTemplate, 'event' | 'enabled'>>> = {
  enviada: {
    subject: 'Tu solicitud VC-0207 quedó en revisión',
    body: 'Hola {{nombre}}: recibimos tu solicitud {{folio}}. Ya está en la bandeja de tu líder; te avisamos en cuanto haya decisión. Mientras siga en revisión puedes cancelarla desde el portal.',
    ctaLabel: 'Ver mi solicitud',
    audience: 'Solicitante',
    timing: 'Inmediato',
    glyph: '↗',
    color: '#4a6fa5',
  },
  lider: {
    subject: 'Marco Rivas solicita vacaciones del 7 al 18 sep',
    body: 'Hola {{lider}}: {{nombre}} pidió vacaciones del {{inicio}} al {{fin}}. En esos días su sub área queda con {{fuera}} personas fuera de {{tope}} permitidas. Revisa el traslape antes de aprobar.',
    ctaLabel: 'Revisar la solicitud',
    audience: 'Líder de sub área',
    timing: 'Inmediato',
    glyph: '⚑',
    color: '#e8930c',
  },
  reemplazo: {
    subject: 'Te designaron como reemplazo de Marco Rivas',
    body: 'Hola {{reemplazo}}: {{nombre}} te designó para cubrir sus pendientes del {{inicio}} al {{fin}}. Abajo va lo que deja en curso.',
    ctaLabel: 'Ver los pendientes',
    audience: 'Reemplazo',
    timing: 'Inmediato',
    glyph: '↺',
    color: '#7c5cff',
  },
  respaldo: {
    subject: 'Bruno Chávez contestó a tu designación de respaldo',
    body: 'Hola {{nombre}}: {{reemplazo}} ya respondió a la designación de respaldo de tu solicitud {{folio}} ({{periodo}}). Si no puede cubrirte, designa a alguien más para que tu líder pueda aprobarla.',
    ctaLabel: 'Ver mi solicitud',
    audience: 'Solicitante',
    timing: 'Al contestar el respaldo',
    glyph: '↩',
    color: '#7c5cff',
  },
  decision: {
    subject: 'Tu solicitud VC-0207 fue aprobada',
    body: 'Hola {{nombre}}: tu solicitud {{folio}} quedó {{estado}}. {{motivo}}',
    ctaLabel: 'Ver el detalle',
    audience: 'Solicitante y reemplazo',
    timing: 'Inmediato',
    glyph: '✓',
    color: '#12a594',
  },
  recordatorio: {
    subject: 'Tus vacaciones inician en 3 días',
    body: 'Hola {{nombre}}: tus días comienzan el {{inicio}}. Deja tus pendientes anotados y confirma con {{reemplazo}} antes de salir.',
    ctaLabel: 'Revisar mis pendientes',
    audience: 'Solicitante y equipo',
    timing: '3 días antes',
    glyph: '⏰',
    color: '#4a6fa5',
  },
  vencimiento: {
    subject: 'Tienes días de vacaciones por vencer',
    body: 'Hola {{nombre}}: {{periodo}}. El periodo se cierra en tu aniversario laboral y lo que no agendes antes de esa fecha se pierde, así que conviene apartarlos ahora aunque sea para más adelante.',
    ctaLabel: 'Agendar mis vacaciones',
    audience: 'Colaborador',
    timing: 'Lunes, si hay días por vencer',
    glyph: '⏳',
    color: '#e8930c',
  },
  imss: {
    subject: 'INC-3402 validada: subsidio registrado',
    body: 'Hola {{nombre}}: la incapacidad {{folio}} fue validada con el IMSS. El subsidio queda registrado y los días no se descuentan de tu saldo de vacaciones.',
    ctaLabel: 'Ver incapacidad',
    audience: 'Colaborador y nómina',
    timing: 'Al validar',
    glyph: '✚',
    color: '#e8930c',
  },
  datos: {
    subject: 'Marco Rivas reportó datos incorrectos en su ficha',
    body: 'Hola: {{nombre}}, de {{area}}, revisó su ficha al entrar al portal y marcó algo que no coincide. El detalle está en Personas y cuentas, en la bandeja de datos reportados.',
    ctaLabel: 'Ver lo que reportó',
    audience: 'Administrador del área',
    timing: 'Inmediato',
    glyph: '!',
    color: '#c0392b',
  },
  reagenda: {
    subject: 'Tus días del {{periodo}} volvieron a tu saldo',
    body: 'Hola {{nombre}}: tu solicitud {{folio}} ({{periodo}}) se cerró como no tomada, así que esos días regresaron a tu saldo y los puedes volver a pedir. Abajo van tres ventanas del mismo tamaño para que elijas; el portal confirma al pedirlas que tu sub área tenga lugar.',
    ctaLabel: 'Pedirlas de nuevo',
    audience: 'Solicitante',
    timing: 'Al cerrarse sin tomarse',
    glyph: '↺',
    color: '#E8930C',
  },
  permiso: {
    subject: '{{nombre}} solicita un permiso ({{periodo}})',
    body: 'Hola {{lider}}: {{nombre}} pidió un permiso para el {{periodo}}. Abajo van el tipo, el horario y si es con goce de sueldo; autorízalo o recházalo en el portal.',
    ctaLabel: 'Aprobar o rechazar',
    audience: 'Líder de sub área',
    timing: 'Inmediato',
    glyph: '⚑',
    color: '#E8930C',
  },
  resumen: {
    subject: 'Quién está fuera esta semana en Tecnología',
    body: 'Resumen de la semana: {{fuera}} personas fuera, {{pendientes}} solicitudes por responder y {{documentos}} documentos pendientes de firma. El detalle por sub área viene abajo.',
    ctaLabel: 'Abrir el panel del área',
    audience: 'Líderes y RR.HH.',
    timing: 'Lunes 8:00',
    glyph: '≡',
    color: '#65748b',
  },
};

const template = (event: EmailEvent): EmailTemplate => ({
  event,
  enabled: event !== 'resumen',
  ...TEMPLATES[event],
});

/*
 * Las escuchas de la configuración, fuera de la clase.
 *
 * Las tenía dentro, privadas, y funcionaba mientras el único que escribía
 * configuración era el propio repositorio. Los catálogos por área los guarda el
 * de organización —es una callable suya— y desde ahí no había forma de avisar:
 * en demo el interruptor se pulsaba, el almacén cambiaba y la pantalla seguía
 * enseñando lo de antes. Medido en el navegador, no deducido.
 */
const configListeners = new Set<(config: RuntimeConfig, fromCache: boolean) => void>();

/** Reparte la configuración vigente. Lo llama quien la haya cambiado. */
const notifyConfig = (): void => {
  for (const listener of configListeners) listener(demoStore.config, false);
};

export class DemoRuntimeConfigRepository implements IRuntimeConfigRepository {

  private get config(): RuntimeConfig {
    return demoStore.config;
  }

  load(): Promise<Result<RuntimeConfig, RepositoryError>> {
    return Promise.resolve(ok(this.config));
  }

  /* Nunca de caché: en demo la configuración es esta memoria, y decir lo
     contrario mandaría al proveedor a pedirla por REST a un Firestore que aquí
     no existe. */
  subscribe(onChange: (config: RuntimeConfig, fromCache: boolean) => void): () => void {
    configListeners.add(onChange);
    queueMicrotask(() => {
      onChange(this.config, false);
    });
    return () => configListeners.delete(onChange);
  }

  /** Applies the patch in memory and notifies, so the OTA behaviour is real here:
   *  toggling a feature really does remove its section from the navigation. */
  save(patch: Partial<RuntimeConfig>): Promise<Result<void, RepositoryError>> {
    demoStore.config = { ...this.config, ...patch };
    notifyConfig();
    return Promise.resolve(ok(undefined));
  }

  /**
   * Agrega el puesto a la memoria y avisa, igual que el servidor: sin duplicar el
   * que ya está aunque venga escrito distinto. **No se guarda en ningún lado** —
   * es el modo demo—, y dura lo que dure la pestaña.
   */
  addPosition(title: string): Promise<Result<AddedPosition, RepositoryError>> {
    const limpio = cleanPositionTitle(title);
    if (!isPositionTitleValid(limpio)) {
      return Promise.resolve(
        err({ kind: 'invalid', what: 'Escribe el nombre del puesto, con al menos tres letras.' }),
      );
    }
    const positions = this.config.catalogs.positions;
    const existing = findPositionByTitle(positions, limpio);
    if (existing !== null) return Promise.resolve(ok({ position: existing, created: false }));

    const position: Position = {
      id: positionKey(limpio).replace(/ /g, '-'),
      title: limpio,
      enabled: true,
    };
    demoStore.config = {
      ...this.config,
      catalogs: { ...this.config.catalogs, positions: [...positions, position] },
    };
    notifyConfig();
    return Promise.resolve(ok({ position, created: true }));
  }

  /**
   * Renders the template but sends nothing, and says so in the subject it returns.
   *
   * Demo mode has no Firestore and no extension, so the honest thing it can report
   * is «rendered». A demo that answered like the real callable would be the very
   * confusion this button was built to end: this screen exists so that «sent» means
   * sent, and a fake that claims it undoes that in the one mode where nobody can
   * check an inbox.
   */
  sendTest(event: EmailEvent): Promise<Result<TestMailReceipt, RepositoryError>> {
    const template = this.config.emailTemplates[event];
    if (template === undefined || !template.enabled) {
      return Promise.resolve(
        err({ kind: 'conflict', what: `la plantilla "${event}" está apagada` }),
      );
    }
    return Promise.resolve(
      ok({
        recipient: 'nadie — el modo demo no envía correo',
        subject: `[DEMO, sin enviar] ${template.subject}`,
      }),
    );
  }
}

/** The seeded runtime configuration, before any edit. */
const initialConfig = (): RuntimeConfig => ({
    identity: DEFAULT_IDENTITY,
    rules: {
      ...DEFAULT_RULES,
      seniority: { ...DEFAULT_RULES.seniority, additionalDays: 4 },
    },
    catalogs: {
      incapacityKinds: DEFAULT_INCAPACITY_KINDS,
      permissionKinds: DEFAULT_PERMISSION_KINDS,
      rejectionReasons: DEFAULT_REJECTION_REASONS,
      positions: DEMO_POSITIONS,
    },
    /*
     * Dos diferencias sembradas a propósito: en demo, Tecnología apaga «Día
     * personal» y **Customer Kare enciende permisos aunque la empresa los tenga
     * apagados**. Sin ellas, la pantalla de catálogos por área se ve igual que la
     * global y no enseña lo único que hay que entender de ella — y el interruptor
     * por área no se puede probar sin tocar la configuración.
     */
    areaCatalogs: {
      tecnologia: {
        areaId: 'tecnologia',
        incapacityKinds: [],
        permissionKinds: [{ id: 'Día personal', enabled: false }],
        features: {},
      },
      'customer-kare': {
        areaId: 'customer-kare',
        incapacityKinds: [],
        permissionKinds: [],
        features: { permisos: true },
      },
    },
    features: { ...DEFAULT_FEATURES },
    rolePermissions: {
      empleado: [...DEFAULT_ROLE_PERMISSIONS.empleado],
      lider: [...DEFAULT_ROLE_PERMISSIONS.lider],
      admin: [...DEFAULT_ROLE_PERMISSIONS.admin],
      finanzas: [...DEFAULT_ROLE_PERMISSIONS.finanzas],
      global: [...DEFAULT_ROLE_PERMISSIONS.global],
      super: [...DEFAULT_ROLE_PERMISSIONS.super],
    },
    emailTemplates: Object.fromEntries(EMAIL_EVENTS.map((e) => [e, template(e)])) as Readonly<
      Record<EmailEvent, EmailTemplate>
    >,
  minClientVersion: '0.0.0',
  maintenanceBanner: null,
});

// ---------------------------------------------------------------------------
// Organisation
// ---------------------------------------------------------------------------

export class DemoOrganizationRepository implements IOrganizationRepository {
  getCompany(): Promise<Result<Company, RepositoryError>> {
    return Promise.resolve(ok(DEMO_COMPANY));
  }

  listAreas(): Promise<Result<readonly Area[], RepositoryError>> {
    return Promise.resolve(ok(demoStore.areas));
  }

  listSubAreas(areaId: AreaId): Promise<Result<readonly SubArea[], RepositoryError>> {
    return Promise.resolve(ok(demoStore.subAreas.filter((s) => s.areaId === areaId)));
  }

  getSubArea(subAreaId: SubAreaId): Promise<Result<SubArea, RepositoryError>> {
    const found = demoStore.subAreas.find((s) => s.id === subAreaId);
    return Promise.resolve(found === undefined ? err(notFound(`subArea ${subAreaId}`)) : ok(found));
  }

  createSubArea(input: {
    areaId: AreaId;
    name: string;
    maxConcurrentOut: number;
  }): Promise<Result<SubAreaId, RepositoryError>> {
    const id = asId<'SubAreaId'>(`${input.areaId}_${input.name.toLowerCase().replace(/\s+/g, '-')}`);
    demoStore.subAreas.push({
      id,
      areaId: input.areaId,
      name: input.name,
      maxConcurrentOut: input.maxConcurrentOut,
      leadPersonId: null,
      color: '#64748B',
      createdAt: new Date(),
      updatedAt: new Date(),
      schemaVersion: 1,
    });
    return Promise.resolve(ok(id));
  }

  updateSubArea(
    subAreaId: SubAreaId,
    patch: { name?: string; maxConcurrentOut?: number; leadPersonId?: PersonId | null },
  ): Promise<Result<void, RepositoryError>> {
    const index = demoStore.subAreas.findIndex((s) => s.id === subAreaId);
    const current = demoStore.subAreas[index];
    if (current !== undefined) {
      demoStore.subAreas[index] = {
        ...current,
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.maxConcurrentOut !== undefined
          ? { maxConcurrentOut: patch.maxConcurrentOut }
          : {}),
        ...(patch.leadPersonId !== undefined ? { leadPersonId: patch.leadPersonId } : {}),
        updatedAt: new Date(),
      };
    }
    return Promise.resolve(ok(undefined));
  }

  deleteSubArea(subAreaId: SubAreaId): Promise<Result<void, RepositoryError>> {
    demoStore.subAreas = demoStore.subAreas.filter((s) => s.id !== subAreaId);
    return Promise.resolve(ok(undefined));
  }

  setAreaAdmin(areaId: AreaId, personId: PersonId): Promise<Result<void, RepositoryError>> {
    const index = demoStore.areas.findIndex((a) => a.id === areaId);
    const current = demoStore.areas[index];
    const persona = demoStore.people.find((p) => p.id === personId);
    if (current !== undefined && persona !== undefined) {
      demoStore.areas[index] = { ...current, adminEmail: persona.email };
      /* Y el rol, como en el servidor: sin esto la demo enseñaría un área con
         administrador y una persona que sigue siendo empleada. */
      const pi = demoStore.people.findIndex((p) => p.id === personId);
      demoStore.people[pi] = { ...persona, role: 'admin' };
    }
    return Promise.resolve(ok(undefined));
  }


  setAreaActive(areaId: AreaId, active: boolean): Promise<Result<void, RepositoryError>> {
    const index = demoStore.areas.findIndex((a) => a.id === areaId);
    const current = demoStore.areas[index];
    if (current !== undefined) demoStore.areas[index] = { ...current, active };
    return Promise.resolve(ok(undefined));
  }

  /* En demo la configuración es memoria, así que esto se ve al instante: apagar
     un tipo aquí lo quita del asistente sin recargar, que es justo lo que hay
     que poder enseñar. */
  saveAreaCatalog(input: SaveAreaCatalogInput): Promise<Result<void, RepositoryError>> {
    demoStore.config = {
      ...demoStore.config,
      areaCatalogs: {
        ...demoStore.config.areaCatalogs,
        [input.areaId]: {
          areaId: input.areaId,
          incapacityKinds: input.incapacityKinds,
          permissionKinds: input.permissionKinds,
          features: input.features,
        },
      },
    };
    notifyConfig();
    return Promise.resolve(ok(undefined));
  }

  createArea(input: {
    name: string;
    adminEmail: string;
    color: string;
  }): Promise<Result<AreaId, RepositoryError>> {
    const id = asId<'AreaId'>(input.name.toLowerCase().replace(/\s+/g, '-'));
    demoStore.areas.push({
      id,
      companyId: DEMO_COMPANY.id,
      name: input.name,
      color: input.color,
      active: true,
      adminEmail: input.adminEmail,
      createdAt: new Date(),
      updatedAt: new Date(),
      schemaVersion: 1,
    });
    return Promise.resolve(ok(id));
  }

  /* El mismo freno que el servidor, y a propósito: en demo se prueba el botón,
     y un borrado que aquí siempre funciona enseña que el área se va sin más. */
  deleteArea(areaId: AreaId): Promise<Result<void, RepositoryError>> {
    const asignadas = demoStore.people.filter((p) => p.areaId === areaId).length;
    if (asignadas > 0) {
      return Promise.resolve(
        err({
          kind: 'conflict',
          what: `el área tiene ${String(asignadas)} persona(s) asignada(s)`,
        }),
      );
    }
    demoStore.subAreas = demoStore.subAreas.filter((s) => s.areaId !== areaId);
    demoStore.areas = demoStore.areas.filter((a) => a.id !== areaId);
    return Promise.resolve(ok(undefined));
  }

  /** Not part of demo mode: the migration writes to Firestore and there is none. */
  importTechnologyRoster(): Promise<Result<RosterImportResult, RepositoryError>> {
    return Promise.resolve(ok({ created: 0, updated: 0, balances: 0, subAreas: 0 }));
  }

}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export class DemoPersonRepository implements IPersonRepository {
  getById(personId: PersonId): Promise<Result<Person, RepositoryError>> {
    const found = demoStore.people.find((p) => p.id === personId);
    return Promise.resolve(found === undefined ? err(notFound(`person ${personId}`)) : ok(found));
  }

  getByUid(uid: string): Promise<Result<Person, RepositoryError>> {
    const found = demoStore.people.find((p) => p.uid === uid);
    return Promise.resolve(found === undefined ? err(notFound(`uid ${uid}`)) : ok(found));
  }

  getByEmail(email: string): Promise<Result<Person, RepositoryError>> {
    const found = demoStore.people.find(
      (p) => p.email.toLowerCase() === email.trim().toLowerCase(),
    );
    return Promise.resolve(found === undefined ? err(notFound(`email ${email}`)) : ok(found));
  }

  list(scope: PersonScope, page: PageRequest): Promise<Result<Page<Person>, RepositoryError>> {
    const items = demoStore.people
      .filter((p) => p.areaId === scope.areaId)
      .filter((p) => scope.subAreaId === undefined || p.subAreaId === scope.subAreaId)
      .filter((p) => scope.status === undefined || p.status === scope.status);
    return Promise.resolve(ok(paged(items, page)));
  }

  getMany(ids: readonly PersonId[]): Promise<Result<readonly Person[], RepositoryError>> {
    return Promise.resolve(
      ok(demoStore.people.filter((p) => ids.includes(p.id))),
    );
  }

  countByStatus(
    areaId: AreaId,
    status: Person['status'],
  ): Promise<Result<number, RepositoryError>> {
    return Promise.resolve(
      ok(demoStore.people.filter((p) => p.areaId === areaId && p.status === status).length),
    );
  }

  updateNotificationPreferences(
    personId: PersonId,
    prefs: Person['notificationPrefs'],
  ): Promise<Result<void, RepositoryError>> {
    this.patch(personId, { notificationPrefs: prefs });
    return Promise.resolve(ok(undefined));
  }

  /**
   * El alta, en memoria.
   *
   * Imita lo que importa para probar la pantalla: compone el nombre con la misma
   * regla del dominio, arma el correo con el dominio de la empresa, rechaza uno
   * repetido y deja la ficha lista para aparecer en el padrón.
   *
   * Lo que **no** imita, y por eso el modo demo no prueba el alta de verdad: no
   * habla con Google Workspace, no crea cuenta de Firebase Auth, no acuña claims
   * y no siembra el saldo del primer periodo. Devuelve `googleAccount: 'skipped'`
   * — que es exactamente lo que el servidor devolverá mientras la delegación de
   * dominio no esté autorizada, así que la pantalla se ejercita contra el mismo
   * caso que va a encontrarse en producción el primer día.
   */
  createPerson(input: CreatePersonInput): Promise<Result<CreatedPerson, RepositoryError>> {
    const name = PersonFullName.create({
      primerNombre: input.primerNombre,
      segundoNombre: input.segundoNombre ?? undefined,
      primerApellido: input.primerApellido,
      segundoApellido: input.segundoApellido ?? undefined,
    });
    if (!name.ok) {
      return Promise.resolve(err({ kind: 'invalid', what: 'Revisa el nombre.' }));
    }

    /*
     * El dominio sale de la configuración, no de una constante de este archivo.
     * Es el mismo sitio del que lo toma el cerco de `beforeSignIn`, así que el
     * demo no puede aceptar un correo que producción rechazaría.
     */
    const domain = demoStore.config.identity.allowedDomains[0] ?? 'knotion.com';
    const email = `${input.emailLocalPart.trim().toLowerCase()}@${domain}`;
    if (demoStore.people.some((p) => p.email.toLowerCase() === email)) {
      return Promise.resolve(err({ kind: 'conflict', what: `Ya hay alguien con ${email}.` }));
    }

    const personId = `p-alta-${String(demoStore.people.length + 1)}` as PersonId;
    const person: Person = {
      ...auditStamp(),
      id: personId,
      /* El alta no los pide todavía: la ficha nace sin ellos y se llenan al
         confirmar los datos, igual que en producción. */
      birthDate: null,
      shirtSize: null,
      parentRole: null,
      // Sin sesión hasta que exista la cuenta: el estado que el modelo ya
      // contempla y que aquí se puede ver de verdad.
      uid: null,
      email,
      displayName: name.value.displayName,
      nameParts: {
        primerNombre: name.value.primerNombre,
        segundoNombre: name.value.segundoNombre,
        primerApellido: name.value.primerApellido,
        segundoApellido: name.value.segundoApellido,
      },
      positionId: input.positionId,
      termination: null,
      areaId: input.areaId,
      subAreaId: input.subAreaId,
      role: input.role,
      documents: {},
      workMode: null,
      computerEquipment: '',
      extraPermissions: [],
      revokedPermissions: [],
      status: 'active',
      hireDate: input.hireDate === null ? null : CalendarDate.fromISOOrThrow(input.hireDate),
      entitledDaysOverride: null,
      photoURL: null,
      notificationPrefs: DEFAULT_NOTIFICATION_PREFERENCES,
    };

    demoStore.people = [...demoStore.people, person];

    return Promise.resolve(
      ok({
        personId,
        email,
        displayName: person.displayName,
        googleAccount: 'skipped' as const,
        googleAccountNote:
          'El modo demo no habla con Google Workspace. En producción esto dirá «creada» cuando la delegación de dominio esté autorizada.',
        googleAccountPassword: null,
      }),
    );
  }

  /**
   * La baja, en memoria — y hace lo que puede de verdad.
   *
   * Registra el hecho, desactiva a la persona y **libera los liderazgos que
   * ejercía**, que es la parte que se olvida: quien se va deja gente sin quien le
   * firme, y dejar sus vínculos en pie produciría solicitudes esperando la firma
   * de alguien que ya no está.
   *
   * Lo que **no** hace, y por eso el demo no prueba una baja de verdad: no cierra
   * el saldo del periodo en curso, no revoca las custom claims —así que su sesión
   * abierta seguiría viva hasta caducar— y no avisa a nadie. Las tres son de
   * servidor.
   */
  terminatePerson(
    input: TerminatePersonInput,
  ): Promise<Result<TerminationResult, RepositoryError>> {
    const person = demoStore.people.find((p) => p.id === input.personId);
    if (person === undefined) {
      return Promise.resolve(err(notFound(`person ${input.personId}`)));
    }
    if (person.termination !== null) {
      return Promise.resolve(
        err({ kind: 'conflict', what: 'Esa persona ya está dada de baja.' }),
      );
    }

    const actor = demoStore.actingPersonId;
    if (actor === null) {
      return Promise.resolve(err({ kind: 'invalid', what: 'No hay sesión.' }));
    }

    const reason = input.reason.trim();
    if (reason.length === 0) {
      return Promise.resolve(err({ kind: 'invalid', what: 'Escribe el motivo.' }));
    }

    // Quién reportaba a quien se va, contado antes de romper los vínculos.
    const asLeader = demoStore.links.filter((l) => l.leaderPersonId === input.personId);
    const subAreasWithoutLead = demoStore.subAreas
      .filter((s) => s.leadPersonId === input.personId)
      .map((s) => s.name);

    this.patch(input.personId, {
      status: 'inactive',
      termination: {
        date: CalendarDate.fromISOOrThrow(input.date),
        reason,
        byPersonId: actor,
      },
    });

    // Se va con sus dos papeles: ni lidera ni reporta.
    demoStore.links = demoStore.links.filter(
      (l) => l.leaderPersonId !== input.personId && l.personId !== input.personId,
    );
    demoStore.subAreas = demoStore.subAreas.map((s) =>
      s.leadPersonId === input.personId ? { ...s, leadPersonId: null } : s,
    );

    return Promise.resolve(
      ok({
        leadershipsReleased: asLeader.length,
        reportsAffected: new Set(asLeader.map((l) => l.personId)).size,
        subAreasWithoutLead,
        /* El modo demo no habla con Google, ni para crear ni para borrar. */
        googleAccount: 'skipped' as const,
        googleAccountNote: input.deleteGoogleAccount
          ? 'El modo demo no habla con Google Workspace. En producción la cuenta se habría borrado.'
          : 'No se pidió borrar la cuenta.',
      }),
    );
  }

  /**
   * Devolver a alguien al padrón, con o sin su antigüedad.
   *
   * Escribe en la bitácora **antes** de limpiar `termination`, y ése es el punto:
   * reactivar borra el único rastro de que la persona se fue alguna vez. Sin la
   * entrada, una baja y su reversión dejarían la ficha idéntica a la de alguien
   * que nunca se movió — y «¿desde cuándo?» volvería a no tener respuesta.
   *
   * Lo que **no** hace, y es de servidor: volver a acuñar sus custom claims, así
   * que en producción la persona seguiría sin poder entrar hasta el siguiente
   * inicio de sesión, y recalcular el saldo cuando la antigüedad se reinicia.
   */
  reactivatePerson(
    input: ReactivatePersonInput,
  ): Promise<Result<ReactivationResult, RepositoryError>> {
    const person = demoStore.people.find((p) => p.id === input.personId);
    if (person === undefined) {
      return Promise.resolve(err(notFound(`person ${input.personId}`)));
    }
    if (person.termination === null) {
      return Promise.resolve(
        err({ kind: 'conflict', what: 'Esa persona no está dada de baja.' }),
      );
    }

    const actor = demoStore.actor();
    /*
     * La fecha de regreso la manda la pantalla y no se toma del reloj: quien
     * captura sabe desde cuándo vuelve, y suele no ser hoy.
     */
    const today = CalendarDate.fromISOOrThrow(input.rehireDate);

    const previousYears =
      person.hireDate === null ? 0 : completedYearsOfService(person.hireDate, today);

    const hireDate = input.keepSeniority ? person.hireDate : today;
    const years = hireDate === null ? 0 : completedYearsOfService(hireDate, today);

    demoStore.auditEntries = [
      {
        ...auditStamp(),
        id: asId<'AuditEntryId'>(`audit-reactivar-${String(demoStore.auditEntries.length + 1)}`),
        areaId: person.areaId,
        areaName: demoStore.areas.find((a) => a.id === person.areaId)?.name ?? '',
        kind: 'Cuenta',
        actorPersonId: actor?.id ?? null,
        actorName: actor?.displayName ?? 'Sistema',
        documentPath: null,
        targetPath: `people/${String(person.id)}`,
        action: `Reactivó a ${person.displayName}`,
        detail: input.keepSeniority
          ? `Conservó su antigüedad desde ${person.hireDate?.toISO() ?? 'sin fecha'}. Su baja del ${person.termination.date.toISO()} decía: ${person.termination.reason}`
          : `Relación laboral nueva desde ${today.toISO()}: pierde ${String(previousYears)} años de antigüedad. Su baja del ${person.termination.date.toISO()} decía: ${person.termination.reason}`,
      },
      ...demoStore.auditEntries,
    ];

    this.patch(input.personId, {
      status: 'active',
      termination: null,
      hireDate,
    });

    return Promise.resolve(
      ok({
        keptSeniority: input.keepSeniority,
        previousYears,
        years,
        hireDate: hireDate?.toISO() ?? '',
      }),
    );
  }

  activateAccount(input: {
    personId: PersonId;
    subAreaId: SubAreaId;
    role: Person['role'];
  }): Promise<Result<void, RepositoryError>> {
    this.patch(input.personId, {
      subAreaId: input.subAreaId,
      role: input.role,
      documents: {},
      workMode: null,
      computerEquipment: '',
      extraPermissions: [],
      revokedPermissions: [],
      status: 'active',
    });
    return Promise.resolve(ok(undefined));
  }

  /** Lo mismo que el servidor, en corto: mismo nombre sin acentos, ficha activa. */
  pendingMatches(pendingId: PersonId): Promise<Result<readonly PendingAccountMatch[], RepositoryError>> {
    const norm = (value: string): string =>
      value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
    const pending = demoStore.people.find((p) => p.id === pendingId);
    if (pending === undefined) return Promise.resolve(ok([]));
    const areaNames = new Map(demoStore.areas.map((area) => [area.id, area.name]));
    return Promise.resolve(
      ok(
        demoStore.people
          .filter((p) => p.id !== pendingId && p.status === 'active')
          .filter((p) => norm(p.displayName) === norm(pending.displayName))
          .map((p) => ({
            personId: p.id,
            displayName: p.displayName,
            email: p.email,
            areaName: areaNames.get(p.areaId) ?? '',
            reason: 'mismo nombre',
          })),
      ),
    );
  }

  mergePendingAccount(input: MergePendingAccountInput): Promise<Result<void, RepositoryError>> {
    const pending = demoStore.people.find((p) => p.id === input.pendingId);
    if (pending === undefined) return Promise.resolve(err(notFound('Esa cuenta ya no existe.')));
    this.patch(input.personId, { email: pending.email, uid: pending.uid });
    demoStore.people = demoStore.people.filter((p) => p.id !== input.pendingId);
    return Promise.resolve(ok(undefined));
  }

  setRole(personId: PersonId, role: Person['role']): Promise<Result<void, RepositoryError>> {
    this.patch(personId, { role });
    return Promise.resolve(ok(undefined));
  }

  setPermissions(input: SetPersonPermissionsInput): Promise<Result<void, RepositoryError>> {
    const i = demoStore.people.findIndex((p) => p.id === input.personId);
    const actual = demoStore.people[i];
    if (actual !== undefined) {
      demoStore.people[i] = {
        ...actual,
        extraPermissions: [...input.extraPermissions],
        revokedPermissions: [...input.revokedPermissions],
      };
    }
    return Promise.resolve(ok(undefined));
  }

  attachDocument(input: AttachPersonDocumentInput): Promise<Result<void, RepositoryError>> {
    const i = demoStore.people.findIndex((p) => p.id === input.personId);
    const actual = demoStore.people[i];
    if (actual !== undefined) {
      demoStore.people[i] = {
        ...actual,
        documents: {
          ...actual.documents,
          [input.kind]: {
            storagePath: input.storagePath,
            uploadedAt: new Date(),
            uploadedByPersonId: demoStore.actingPersonId,
          },
        },
      };
    }
    return Promise.resolve(ok(undefined));
  }

  /**
   * El traspaso, en memoria y con las mismas consecuencias.
   *
   * El demo hace lo mismo que el servidor —cancelar lo que está en vuelo, rehacer
   * los vínculos, mover el saldo— porque un demo que sólo cambia el área enseñaría
   * un traspaso que no existe: sin cancelar nada y con los líderes viejos intactos.
   */
  transferArea(input: TransferAreaInput): Promise<Result<TransferAreaResult, RepositoryError>> {
    const person = demoStore.people.find((p) => p.id === input.personId);
    if (person === undefined) return Promise.resolve(err({ kind: 'not-found', what: 'La persona' }));

    const enVuelo = new Set(['Por revisar', 'En revisión', 'Visto bueno']);
    let cancelledVacations = 0;
    for (const r of demoStore.requests) {
      if (r.personId === input.personId && enVuelo.has(r.status)) {
        (r as { status: string }).status = 'Cancelada';
        cancelledVacations += 1;
      }
    }

    let cancelledPermits = 0;
    for (const r of demoStore.permissions) {
      if (r.personId === input.personId && r.status === 'Por aprobar') {
        (r as { status: string }).status = 'Cancelado';
        cancelledPermits += 1;
      }
    }

    const removedLeaders = demoStore.links.filter((l) => l.personId === input.personId).length;
    /* Y a quién lideraba: se van los dos sentidos, como en el servidor. */
    const releasedReports = new Set(
      demoStore.links.filter((l) => l.leaderPersonId === input.personId).map((l) => l.personId),
    ).size;
    demoStore.links = demoStore.links.filter(
      (l) => l.personId !== input.personId && l.leaderPersonId !== input.personId,
    );
    for (const leaderPersonId of input.leaderPersonIds) {
      demoStore.links.push({
        id: asId<'PersonLeaderLinkId'>(`${input.personId}_${leaderPersonId}`),
        personId: input.personId,
        leaderPersonId,
        areaId: input.toAreaId,
        projectLabel: DEMO_LEADER_PROJECTS[leaderPersonId] ?? 'Proyecto por definir',
        createdAt: new Date(),
        updatedAt: new Date(),
        schemaVersion: 1,
      });
    }

    for (const b of demoStore.balances) {
      if (b.personId === input.personId) (b as { areaId: string }).areaId = input.toAreaId;
    }

    (person as { areaId: string; subAreaId: string | null }).areaId = input.toAreaId;
    (person as { areaId: string; subAreaId: string | null }).subAreaId = input.toSubAreaId;

    return Promise.resolve(
      ok({ cancelledVacations, cancelledPermits, removedLeaders, releasedReports }),
    );
  }

  setSubArea(
    personId: PersonId,
    subAreaId: SubAreaId | null,
  ): Promise<Result<void, RepositoryError>> {
    this.patch(personId, { subAreaId });
    return Promise.resolve(ok(undefined));
  }

  /**
   * Corrige la ficha en memoria.
   *
   * Y repasa las copias desnormalizadas del nombre, que aquí sería fácil omitir:
   * sin eso el demo enseñaría el nombre nuevo en la ficha y el viejo en las
   * tarjetas — un fallo inventado por el falso, que es la clase de divergencia que
   * ya escondió cuatro errores reales.
   *
   * Lo que **no** imita es el resto: no toca Auth, no valida el dominio del correo,
   * no comprueba que nadie más lo tenga y no mueve el periodo del saldo. El modo
   * demo sirve para mirar la pantalla, no para creerle a esta operación.
   */
  updateDetail(
    personId: PersonId,
    patch: PersonDetailPatch,
  ): Promise<Result<PersonDetailUpdate, RepositoryError>> {
    const before = demoStore.people.find((p) => p.id === personId);
    if (before === undefined) {
      return Promise.resolve(err({ kind: 'not-found', what: `people/${personId}` }));
    }

    // Un literal de una vez y no asignaciones sueltas: los campos de `Person` son
    // de sólo lectura, que es lo que impide que un adaptador mute una entidad en
    // el sitio y se la deje cambiada a quien la estuviera leyendo.
    const changes: Partial<Person> = {
      ...(patch.displayName === undefined ? {} : { displayName: patch.displayName }),
      ...(patch.email === undefined ? {} : { email: patch.email }),
      ...(patch.hireDate === undefined
        ? {}
        : { hireDate: CalendarDate.fromISOOrThrow(patch.hireDate) }),
      /* Los tres que se pueden vaciar: ausente no toca, `null` vacía. */
      ...(patch.birthDate === undefined
        ? {}
        : {
            birthDate:
              patch.birthDate === null ? null : CalendarDate.fromISOOrThrow(patch.birthDate),
          }),
      ...(patch.shirtSize === undefined ? {} : { shirtSize: patch.shirtSize }),
      ...(patch.parentRole === undefined ? {} : { parentRole: patch.parentRole }),
      ...(patch.positionId === undefined ? {} : { positionId: patch.positionId }),
    };
    this.patch(personId, changes);

    const renamed = patch.displayName;
    if (renamed !== undefined) {
      demoStore.requests = demoStore.requests.map((request) =>
        request.personId === personId ? { ...request, personName: renamed } : request,
      );
    }

    const period = patch.hireDate === undefined ? null : Number(patch.hireDate.slice(0, 4));

    return Promise.resolve(
      ok({
        changed: Object.keys(changes).length > 0,
        periodMoved:
          period === null
            ? null
            : { previousPeriod: before.hireDate?.year ?? null, period },
      }),
    );
  }

  adjustBalance(
    personId: PersonId,
    adjustment: BalanceAdjustment,
  ): Promise<Result<BalanceAdjusted, RepositoryError>> {
    const current = demoStore.balances.find(
      (b) =>
        b.personId === personId &&
        (adjustment.periodYear === undefined || b.year === adjustment.periodYear),
    );

    if (current !== undefined && adjustment.openingUsedDays !== undefined) {
      const opening = adjustment.openingUsedDays;
      demoStore.balances = demoStore.balances.map((balance) =>
        balance === current ? { ...balance, openingUsedDays: opening } : balance,
      );
    }

    return Promise.resolve(
      ok({
        period: current?.year ?? new Date().getFullYear(),
        entitledDays: adjustment.entitledDaysOverride ?? current?.entitledDays ?? 0,
        openingUsedDays: adjustment.openingUsedDays ?? current?.openingUsedDays ?? 0,
      }),
    );
  }

  private patch(personId: PersonId, changes: Partial<Person>): void {
    const index = demoStore.people.findIndex((p) => p.id === personId);
    const current = demoStore.people[index];
    if (current !== undefined) {
      demoStore.people[index] = { ...current, ...changes, updatedAt: new Date() };
    }
  }
}

/**
 * El salario en memoria.
 *
 * Un `Map` aparte del padrón, igual que en producción es una colección aparte: si
 * el falso lo colgara de la persona, la pantalla se vería bien y escondería
 * justo la fuga que motivó separarlo. Es la clase de divergencia entre falso y
 * real que ya escondió cuatro errores en este proyecto.
 */
/**
 * El expediente en demo. Vive en memoria y se va con la pestaña, como todo lo
 * demás de aquí: sirve para mirar la pantalla, no para creerle nada.
 */
export class DemoPersonalRecordRepository implements IPersonalRecordRepository {
  getFor(personId: PersonId): Promise<Result<PersonalRecord | null, RepositoryError>> {
    return Promise.resolve(ok(demoStore.personalRecords.get(personId) ?? null));
  }

  save(input: SavePersonalRecordInput): Promise<Result<void, RepositoryError>> {
    const previo = demoStore.personalRecords.get(input.personId);
    demoStore.personalRecords.set(input.personId, {
      personId: input.personId,
      maritalStatus: isMaritalStatus(input.maritalStatus) ? input.maritalStatus : null,
      hasInfonavitCredit: input.hasInfonavitCredit,
      educationLevel: isEducationLevel(input.educationLevel) ? input.educationLevel : null,
      bloodType: isBloodType(input.bloodType) ? input.bloodType : null,
      children: input.children,
      allergies: input.allergies,
      personalEmail: input.personalEmail,
      personalPhone: input.personalPhone,
      privacyAcceptedAt: previo?.privacyAcceptedAt ?? null,
      updatedAt: new Date(),
      updatedByPersonId: demoStore.actingPersonId,
    });
    return Promise.resolve(ok(undefined));
  }
}

/**
 * La confirmación de datos en demo.
 *
 * Sirve para ver la tarjeta y la bandeja, no para creerle nada: aquí no hay
 * reglas que evalúen el alcance ni correo que salga al administrador del área,
 * que son las dos mitades que de verdad deciden si esto funciona.
 */
export class DemoDataConfirmationRepository implements IDataConfirmationRepository {
  getFor(personId: PersonId): Promise<Result<DataConfirmation | null, RepositoryError>> {
    return Promise.resolve(ok(demoStore.dataConfirmations.get(personId) ?? null));
  }

  listReported(areaId: AreaId | null): Promise<Result<readonly DataConfirmation[], RepositoryError>> {
    const todas = [...demoStore.dataConfirmations.values()].filter(
      (item) => item.corrections.length > 0,
    );
    return Promise.resolve(
      ok(areaId === null ? todas : todas.filter((item) => item.areaId === areaId)),
    );
  }

  submit(input: SubmitDataConfirmationInput): Promise<Result<void, RepositoryError>> {
    const personId = demoStore.actingPersonId;
    if (personId === null) return Promise.resolve(ok(undefined));
    const persona = demoStore.people.find((p) => p.id === personId);

    /* Lo mismo que hace el servidor: la respuesta se escribe en la ficha, no se
       reporta. Sin esto el demo enseñaría una casilla que no guarda nada, que es
       justo lo que el modo demo hace peor — mentir en la dirección tranquila. */
    if (input.parentRole !== undefined && persona !== undefined) {
      const i = demoStore.people.indexOf(persona);
      demoStore.people[i] = { ...persona, parentRole: input.parentRole };
    }

    demoStore.dataConfirmations.set(personId, {
      personId,
      areaId: persona?.areaId ?? asId<'AreaId'>(''),
      personName: persona?.displayName ?? '',
      confirmedAt: new Date(),
      version: CONFIRMATION_VERSION,
      corrections: cleanCorrections(
        input.corrections.filter((item) => isConfirmableField(item.field)) as readonly {
          field: ConfirmableField;
          shown: string;
          correct: string;
        }[],
      ),
      resolvedAt: null,
      resolvedByPersonId: null,
    });
    return Promise.resolve(ok(undefined));
  }

  resolve(personId: PersonId): Promise<Result<void, RepositoryError>> {
    const actual = demoStore.dataConfirmations.get(personId);
    if (actual !== undefined) {
      demoStore.dataConfirmations.set(personId, {
        ...actual,
        resolvedAt: new Date(),
        resolvedByPersonId: demoStore.actingPersonId,
      });
    }
    return Promise.resolve(ok(undefined));
  }
}

export class DemoSalaryRepository implements ISalaryRepository {
  getFor(personId: PersonId): Promise<Result<SalaryRecord | null, RepositoryError>> {
    return Promise.resolve(ok(demoStore.salaries.get(personId) ?? null));
  }

  set(input: SetSalaryInput): Promise<Result<void, RepositoryError>> {
    if (!Number.isInteger(input.monthlyCents) || input.monthlyCents <= 0) {
      return Promise.resolve(err({ kind: 'invalid', what: 'El sueldo tiene que ser mayor que cero.' }));
    }
    const actor = demoStore.actingPersonId;
    if (actor === null) {
      return Promise.resolve(err({ kind: 'invalid', what: 'No hay sesión.' }));
    }
    demoStore.salaries.set(input.personId, {
      personId: input.personId,
      monthlyCents: input.monthlyCents,
      effectiveFrom: CalendarDate.fromISOOrThrow(input.effectiveFrom),
      setByPersonId: actor,
      updatedAt: new Date(),
    });
    return Promise.resolve(ok(undefined));
  }
}

export class DemoPersonLeaderRepository implements IPersonLeaderRepository {
  leadersOf(
    personId: PersonId,
    areaId: AreaId,
  ): Promise<Result<readonly PersonLeaderLink[], RepositoryError>> {
    /* Filtra por las dos, como la de verdad: si el demo ignorara `areaId`, un
       llamador que pasara la equivocada funcionaría aquí y fallaría en producción,
       que es justo el fallo que este parámetro existe para no repetir. */
    return Promise.resolve(
      ok(demoStore.links.filter((l) => l.personId === personId && l.areaId === areaId)),
    );
  }

  reportsOf(
    leaderPersonId: PersonId,
  ): Promise<Result<readonly PersonLeaderLink[], RepositoryError>> {
    return Promise.resolve(
      ok(demoStore.links.filter((l) => l.leaderPersonId === leaderPersonId)),
    );
  }

  listByArea(areaId: AreaId): Promise<Result<readonly PersonLeaderLink[], RepositoryError>> {
    return Promise.resolve(ok(demoStore.links.filter((l) => l.areaId === areaId)));
  }

  link(personId: PersonId, leaderPersonId: PersonId): Promise<Result<void, RepositoryError>> {
    const id = `${personId}_${leaderPersonId}`;
    if (!demoStore.links.some((l) => l.id === id)) {
      demoStore.links.push({
        id,
        personId,
        leaderPersonId,
        areaId: demoStore.people.find((p) => p.id === personId)?.areaId ?? asId<'AreaId'>(''),
        projectLabel: DEMO_LEADER_PROJECTS[leaderPersonId] ?? 'Proyecto por definir',
        createdAt: new Date(),
        updatedAt: new Date(),
        schemaVersion: 1,
      });
    }
    return Promise.resolve(ok(undefined));
  }

  unlink(personId: PersonId, leaderPersonId: PersonId): Promise<Result<void, RepositoryError>> {
    demoStore.links = demoStore.links.filter(
      (l) => !(l.personId === personId && l.leaderPersonId === leaderPersonId),
    );
    return Promise.resolve(ok(undefined));
  }
}

// ---------------------------------------------------------------------------
// Vacation requests
// ---------------------------------------------------------------------------

export class DemoVacationRequestRepository implements IVacationRequestRepository {
  getById(requestId: RequestId): Promise<Result<VacationRequest, RepositoryError>> {
    const found = demoStore.requests.find((r) => r.id === requestId);
    return Promise.resolve(found === undefined ? err(notFound(`request ${requestId}`)) : ok(found));
  }

  getByFolio(folio: string): Promise<Result<VacationRequest, RepositoryError>> {
    const found = demoStore.requests.find((r) => r.folio === folio);
    return Promise.resolve(found === undefined ? err(notFound(`folio ${folio}`)) : ok(found));
  }

  list(
    scope: RequestScope,
    page: PageRequest,
  ): Promise<Result<Page<VacationRequest>, RepositoryError>> {
    return Promise.resolve(ok(paged(this.filter(scope), page)));
  }

  count(scope: RequestScope): Promise<Result<number, RepositoryError>> {
    return Promise.resolve(ok(this.filter(scope).length));
  }

  private filter(scope: RequestScope): VacationRequest[] {
    return demoStore.requests
      .filter((r) => scope.areaId === undefined || r.areaId === scope.areaId)
      .filter((r) => scope.subAreaId === undefined || r.subAreaId === scope.subAreaId)
      .filter((r) => scope.personId === undefined || r.personId === scope.personId)
      .filter(
        (r) =>
          scope.replacementPersonId === undefined ||
          r.replacementPersonId === scope.replacementPersonId,
      )
      .filter((r) => scope.statuses === undefined || scope.statuses.includes(r.status))
      .filter((r) => scope.touching === undefined || r.range.overlaps(scope.touching))
      .sort((a, b) => b.range.start.compare(a.range.start));
  }

  approvalsFor(requestId: RequestId): Promise<Result<readonly RequestApproval[], RepositoryError>> {
    return Promise.resolve(
      ok(demoStore.approvals.filter((a) => a.requestId === requestId).sort((a, b) => a.order - b.order)),
    );
  }

  workItemsFor(requestId: RequestId): Promise<Result<readonly RequestWorkItem[], RepositoryError>> {
    return Promise.resolve(ok(demoStore.workItems.filter((w) => w.requestId === requestId)));
  }

  submit(
    input: SubmitRequestInput,
  ): Promise<
    Result<
      { requestId: RequestId; folio: string; businessDays: number; requiredSignatures: 1 | 2 },
      RepositoryError
    >
  > {
    const actor = demoStore.actor();
    if (actor === undefined) {
      return Promise.resolve(err({ kind: 'permission-denied', what: 'sesión demo' }));
    }

    const nonWorking = enabledHolidayDates(demoStore.holidays);
    const businessDays = countBusinessDays(input.range, nonWorking);

    /*
     * How many signatures, decided here and frozen — the same rule the real
     * backend applies. Turning the feature off later must not retroactively
     * half-approve a request already in flight.
     */
    const leaderCount = demoStore.links.filter((l) => l.personId === actor.id).length;
    const requiredSignatures: 1 | 2 =
      demoStore.config.features.doblevb && leaderCount >= 2 ? 2 : 1;

    const replacement =
      input.replacementPersonId === null
        ? undefined
        : demoStore.people.find((p) => p.id === input.replacementPersonId);

    const folio = demoStore.nextFolio('VC', demoStore.requests, 4);
    const requestId = asId<'RequestId'>(`req-${folio}`);

    demoStore.requests = [
      {
        ...auditStamp(),
        id: requestId,
        folio,
        personId: actor.id,
        personName: actor.displayName,
        areaId: actor.areaId,
        subAreaId: actor.subAreaId,
        subAreaName: subAreaNameOf(actor.subAreaId),
        range: input.range,
        businessDays,
        // Nombrar a alguien no es que haya aceptado: nace pendiente.
        replacementDecision: input.replacementPersonId === null ? null : 'pending',
        replacementReason: null,
        replacementDecidedAt: null,
        replacementWaivedReason: null,
        replacementPersonId: input.replacementPersonId,
        replacementName: replacement?.displayName ?? '',
        status: 'Por revisar',
        pdfPath: null,
        notes: input.notes,
        requiredSignatures,
        resolutionReason: null,
        decidedAt: null,
        rescheduledFromFolio: input.rescheduledFromFolio,
        assignedByName: null,
        assignedByPersonId: null,
        submittedAt: new Date(),
      },
      ...demoStore.requests,
    ];

    demoStore.workItems = [
      ...demoStore.workItems,
      ...input.workItems
        .filter(
          (item: { ticket: string; text: string }) =>
            item.ticket.trim().length > 0 || item.text.trim().length > 0,
        )
        .map((item: { ticket: string; text: string }, index: number) => ({
          ...auditStamp(),
          id: asId<'WorkItemId'>(`wi-${folio}-${String(index)}`),
          requestId,
          ticket: item.ticket,
          text: item.text,
        })),
    ];

    return Promise.resolve(ok({ requestId, folio, businessDays, requiredSignatures }));
  }

  /**
   * El registro del área, en demo. Nace aprobada, como en el servidor.
   *
   * No comprueba el saldo ni los choques: eso lo decide el servidor y la demo no
   * evalúa reglas. Sirve para mirar la pantalla, no para creerle nada.
   */
  assign(input: AssignVacationInput): Promise<Result<AssignVacationResult, RepositoryError>> {
    const actor = demoStore.actor();
    if (actor === undefined) {
      return Promise.resolve(err({ kind: 'permission-denied', what: 'sesión demo' }));
    }
    const persona = demoStore.people.find((p) => p.id === input.personId);
    if (persona === undefined) {
      return Promise.resolve(err({ kind: 'not-found', what: 'La persona no existe.' }));
    }

    const range = DateRange.fromISOOrThrow(input.startDate, input.endDate);
    const businessDays = countBusinessDays(range, enabledHolidayDates(demoStore.holidays));
    const folio = demoStore.nextFolio('VC', demoStore.requests, 4);
    const requestId = asId<'RequestId'>(`req-${folio}`);

    demoStore.requests = [
      {
        ...auditStamp(),
        id: requestId,
        folio,
        personId: persona.id,
        personName: persona.displayName,
        areaId: persona.areaId,
        subAreaId: persona.subAreaId,
        subAreaName: subAreaNameOf(persona.subAreaId),
        range,
        businessDays,
        replacementDecision: null,
        replacementReason: null,
        replacementDecidedAt: null,
        replacementWaivedReason: null,
        replacementPersonId: null,
        replacementName: '',
        status: 'Aprobada',
        pdfPath: null,
        notes: input.reason,
        requiredSignatures: 1,
        resolutionReason: null,
        decidedAt: new Date(),
        rescheduledFromFolio: null,
        assignedByName: actor.displayName,
        /* La autoría, que es lo que después decide quién puede borrarla. */
        assignedByPersonId: actor.id,
        submittedAt: new Date(),
      },
      ...demoStore.requests,
    ];

    /* La firma de quien la registró. Sin ella la tarjeta diría «Sin firmas
       todavía» sobre unas vacaciones que ya están aprobadas — y eso es lo que
       enseñaba esta demo antes de añadirlo. */
    demoStore.approvals = [
      ...demoStore.approvals,
      {
        ...auditStamp(),
        id: asId<'ApprovalId'>(`ap-${folio}-1`),
        requestId,
        approverPersonId: actor.id,
        approverName: actor.displayName,
        order: 1,
        decision: 'approved',
        decidedAt: new Date(),
        reason: `Registrada por el área: ${input.reason}`,
      },
    ];

    /* El demo no lleva saldos por periodo: nunca queda a deber. */
    return Promise.resolve(ok({ folio, businessDays, owedDays: 0 }));
  }

  /** Applies the transition locally so the board visibly moves. */
  decide(input: {
    requestId: RequestId;
    decision: 'approve' | 'reject';
    reason: string | null;
    waiveReplacementReason: string | null;
  }): Promise<Result<void, RepositoryError>> {
    const index = demoStore.requests.findIndex((r) => r.id === input.requestId);
    const current = demoStore.requests[index];
    if (current === undefined) return Promise.resolve(err(notFound('request')));

    const signed = demoStore.approvals.filter(
      (a) => a.requestId === input.requestId && a.decision === 'approved',
    ).length;

    const nextStatus =
      input.decision === 'reject'
        ? 'Rechazada'
        : signed + 1 >= current.requiredSignatures
          ? 'Aprobada'
          : 'Visto bueno';

    demoStore.requests[index] = {
      ...current,
      status: nextStatus,
      resolutionReason: input.reason,
      // La constancia de haber aprobado sin confirmación del respaldo. Sin esto
      // el modo demo enseñaría un candado que no deja rastro, que es media
      // función y la mitad que no se ve.
      ...(input.waiveReplacementReason === null
        ? {}
        : { replacementWaivedReason: input.waiveReplacementReason }),
      updatedAt: new Date(),
    };

    const pendingIndex = demoStore.approvals.findIndex(
      (a) => a.requestId === input.requestId && a.decision === 'pending',
    );
    const pending = demoStore.approvals[pendingIndex];
    if (pending !== undefined) {
      demoStore.approvals[pendingIndex] = {
        ...pending,
        decision: input.decision === 'approve' ? 'approved' : 'rejected',
        decidedAt: new Date(),
        reason: input.reason,
      };
    }

    return Promise.resolve(ok(undefined));
  }

  respondToReplacement(input: {
    requestId: RequestId;
    answer: 'accept' | 'decline';
    reason: string | null;
  }): Promise<Result<void, RepositoryError>> {
    const index = demoStore.requests.findIndex((r) => r.id === input.requestId);
    const current = demoStore.requests[index];
    if (current !== undefined) {
      demoStore.requests[index] = {
        ...current,
        replacementDecision: input.answer === 'accept' ? 'accepted' : 'declined',
        replacementReason: input.answer === 'accept' ? null : input.reason,
        replacementDecidedAt: new Date(),
        updatedAt: new Date(),
      };
    }
    return Promise.resolve(ok(undefined));
  }

  changeReplacement(
    requestId: RequestId,
    replacementPersonId: PersonId,
  ): Promise<Result<void, RepositoryError>> {
    const index = demoStore.requests.findIndex((r) => r.id === requestId);
    const current = demoStore.requests[index];
    const person = demoStore.people.find((p) => p.id === replacementPersonId);
    if (current !== undefined && person !== undefined) {
      demoStore.requests[index] = {
        ...current,
        replacementPersonId,
        replacementName: person.displayName,
        // El nuevo tampoco ha dicho que sí: vuelve a empezar la pregunta.
        replacementDecision: 'pending',
        replacementReason: null,
        replacementDecidedAt: null,
        updatedAt: new Date(),
      };
    }
    return Promise.resolve(ok(undefined));
  }

  cancel(input: { requestId: RequestId; reason: string }): Promise<Result<void, RepositoryError>> {
    const index = demoStore.requests.findIndex((r) => r.id === input.requestId);
    const current = demoStore.requests[index];
    if (current !== undefined) {
      demoStore.requests[index] = {
        ...current,
        status: 'Cancelada',
        resolutionReason: input.reason,
        updatedAt: new Date(),
      };
    }
    return Promise.resolve(ok(undefined));
  }

  resolveOverdue(
    input: ResolveOverdueInput,
  ): Promise<Result<ResolveOverdueResult, RepositoryError>> {
    const index = demoStore.requests.findIndex((r) => r.id === input.requestId);
    const current = demoStore.requests[index];
    if (current === undefined) return Promise.resolve(ok({ estado: '—', diasDevueltos: 0, ventanas: [] }));

    const estado = input.outcome === 'tomada' ? 'Tomada' : 'Cancelada';
    demoStore.requests[index] = {
      ...current,
      status: estado,
      resolutionReason: input.reason,
      updatedAt: new Date(),
    };
    return Promise.resolve(
      ok({
        estado,
        diasDevueltos: input.outcome === 'no-tomada' ? current.businessDays : 0,
        ventanas: [],
      }),
    );
  }

  /**
   * Borra de verdad, como el servidor: la solicitud desaparece de la lista.
   *
   * La regla de autoría se hace valer en el servidor; aquí sólo se reproduce el
   * efecto, que es lo que el demo existe para enseñar. Y se comprueba igual antes
   * de quitar nada — un demo que borrara lo que el servidor rechaza enseñaría un
   * botón que en producción da error.
   */
  deleteAssigned(requestId: RequestId): Promise<Result<void, RepositoryError>> {
    const actor = demoStore.actingPersonId;
    const encontrada = demoStore.requests.find((r) => r.id === requestId);
    if (encontrada === undefined || actor === null) return Promise.resolve(ok(undefined));
    if (!canDeleteAssignedVacation(encontrada, actor)) {
      return Promise.resolve(
        err({
          kind: 'permission-denied',
          what: 'Solo quien registró unas vacaciones puede borrarlas.',
        }),
      );
    }
    demoStore.requests = demoStore.requests.filter((r) => r.id !== requestId);
    return Promise.resolve(ok(undefined));
  }

  /*
   * The demo has no server to push anything, so this reports once and stops.
   * Returning a live-looking listener that never fires would make the screens
   * wait for an event that cannot arrive.
   */
  subscribeToPerson(
    personId: PersonId,
    onChange: (requests: readonly VacationRequest[]) => void,
  ): () => void {
    onChange(demoStore.requests.filter((request) => request.personId === personId));
    return () => undefined;
  }

  /** The demo pushes nothing; it reports the current contents once. */
  subscribeToScope(
    scope: RequestScope,
    onChange: (requests: readonly VacationRequest[]) => void,
  ): () => void {
    void scope;
    onChange(demoStore.requests);
    return () => undefined;
  }

  moveToColumn(
    requestId: RequestId,
    status: VacationRequest['status'],
  ): Promise<Result<void, RepositoryError>> {
    const index = demoStore.requests.findIndex((r) => r.id === requestId);
    const current = demoStore.requests[index];
    if (current !== undefined) {
      demoStore.requests[index] = { ...current, status, updatedAt: new Date() };
    }
    return Promise.resolve(ok(undefined));
  }

  /**
   * Undoes an approval in memory the way the callable does on the server: the
   * signatures go back to pending and the request returns to a review column.
   */
  reopen(input: {
    requestId: string;
    status: 'Por revisar' | 'En revisión';
  }): Promise<Result<void, RepositoryError>> {
    const index = demoStore.requests.findIndex((r) => r.id === input.requestId);
    const current = demoStore.requests[index];
    if (current !== undefined) {
      demoStore.requests[index] = {
        ...current,
        status: input.status,
        resolutionReason: null,
        updatedAt: new Date(),
      };
      demoStore.approvals = demoStore.approvals.map((a) =>
        a.requestId === input.requestId
          ? { ...a, decision: 'pending' as const, decidedAt: null, reason: null }
          : a,
      );
    }
    return Promise.resolve(ok(undefined));
  }
}

// ---------------------------------------------------------------------------
// Permissions and incapacities
// ---------------------------------------------------------------------------

export class DemoPermissionRequestRepository implements IPermissionRequestRepository {
  getById(id: string): Promise<Result<PermissionRequest, RepositoryError>> {
    const found = demoStore.permissions.find((p) => p.id === id);
    return Promise.resolve(found === undefined ? err(notFound(`permission ${id}`)) : ok(found));
  }

  list(
    scope: PermissionScope,
    page: PageRequest,
  ): Promise<Result<Page<PermissionRequest>, RepositoryError>> {
    return Promise.resolve(ok(paged(this.filter(scope), page)));
  }

  count(scope: PermissionScope): Promise<Result<number, RepositoryError>> {
    return Promise.resolve(ok(this.filter(scope).length));
  }

  private filter(scope: PermissionScope): PermissionRequest[] {
    return demoStore.permissions
      .filter((p) => scope.areaId === undefined || p.areaId === scope.areaId)
      .filter((p) => scope.subAreaId === undefined || p.subAreaId === scope.subAreaId)
      .filter((p) => scope.personId === undefined || p.personId === scope.personId)
      .filter((p) => scope.statuses === undefined || scope.statuses.includes(p.status))
      .filter((p) => scope.touching === undefined || p.range.overlaps(scope.touching))
      .sort((a, b) => b.range.start.compare(a.range.start));
  }

  submit(input: SubmitPermissionInput): Promise<Result<PermissionRequestId, RepositoryError>> {
    const actor = demoStore.actor();
    if (actor === undefined) {
      return Promise.resolve(err({ kind: 'permission-denied', what: 'sesión demo' }));
    }

    const kind = demoStore.config.catalogs.permissionKinds.find((k) => k.id === input.kind);
    const folio = demoStore.nextFolio('PER', demoStore.permissions, 4);
    const id = asId<'PermissionRequestId'>(`perm-${folio}`);

    demoStore.permissions = [
      {
        ...auditStamp(),
        id,
        folio,
        personId: actor.id,
        personName: actor.displayName,
        areaId: actor.areaId,
        subAreaId: actor.subAreaId,
        subAreaName: subAreaNameOf(actor.subAreaId),
        kind: input.kind,
        range: input.range,
        hours: input.hours,
        // Copied from the catalogue at creation: renaming or reconfiguring the
        // kind later must not rewrite what was already granted under it.
        withPay: kind?.withPay ?? true,
        reason: input.reason,
        status: 'Por aprobar',
        decidedByPersonId: null,
        decidedByName: '',
        decidedAt: null,
        resolutionReason: null,
      },
      ...demoStore.permissions,
    ];

    return Promise.resolve(ok(id));
  }

  decide(input: {
    id: string;
    decision: 'approve' | 'reject';
    reason: string | null;
  }): Promise<Result<void, RepositoryError>> {
    const index = demoStore.permissions.findIndex((p) => p.id === input.id);
    const current = demoStore.permissions[index];
    if (current !== undefined) {
      demoStore.permissions[index] = {
        ...current,
        status: input.decision === 'approve' ? 'Aprobado' : 'Rechazado',
        resolutionReason: input.reason,
        decidedAt: new Date(),
        updatedAt: new Date(),
      };
    }
    return Promise.resolve(ok(undefined));
  }

  reopen(id: string): Promise<Result<void, RepositoryError>> {
    const index = demoStore.permissions.findIndex((p) => p.id === id);
    const current = demoStore.permissions[index];
    if (current !== undefined) {
      demoStore.permissions[index] = {
        ...current,
        status: 'Por aprobar',
        decidedByPersonId: null,
        decidedByName: '',
        decidedAt: null,
        resolutionReason: null,
        updatedAt: new Date(),
      };
    }
    return Promise.resolve(ok(undefined));
  }

  cancel(id: string, reason: string): Promise<Result<void, RepositoryError>> {
    const index = demoStore.permissions.findIndex((p) => p.id === id);
    const current = demoStore.permissions[index];
    if (current !== undefined) {
      demoStore.permissions[index] = {
        ...current,
        status: 'Cancelado',
        resolutionReason: reason,
        updatedAt: new Date(),
      };
    }
    return Promise.resolve(ok(undefined));
  }
}

export class DemoIncapacityRepository implements IIncapacityRepository {
  getById(id: string): Promise<Result<Incapacity, RepositoryError>> {
    const found = demoStore.incapacities.find((i) => i.id === id);
    return Promise.resolve(found === undefined ? err(notFound(`incapacity ${id}`)) : ok(found));
  }

  list(
    scope: IncapacityScope,
    page: PageRequest,
  ): Promise<Result<Page<Incapacity>, RepositoryError>> {
    return Promise.resolve(ok(paged(this.filter(scope), page)));
  }

  count(scope: IncapacityScope): Promise<Result<number, RepositoryError>> {
    return Promise.resolve(ok(this.filter(scope).length));
  }

  private filter(scope: IncapacityScope): Incapacity[] {
    return demoStore.incapacities
      .filter((i) => scope.areaId === undefined || i.areaId === scope.areaId)
      .filter((i) => scope.subAreaId === undefined || i.subAreaId === scope.subAreaId)
      .filter((i) => scope.personId === undefined || i.personId === scope.personId)
      .filter((i) => scope.statuses === undefined || scope.statuses.includes(i.status))
      .sort((a, b) => b.range.start.compare(a.range.start));
  }

  register(input: RegisterIncapacityInput): Promise<Result<IncapacityId, RepositoryError>> {
    const actor = demoStore.actor();
    if (actor === undefined) {
      return Promise.resolve(err({ kind: 'permission-denied', what: 'sesión demo' }));
    }

    const kind = demoStore.config.catalogs.incapacityKinds.find((k) => k.id === input.kind);
    const folio = demoStore.nextFolio('INC', demoStore.incapacities, 4);
    const id = asId<'IncapacityId'>(`inc-${folio}`);

    demoStore.incapacities = [
      {
        ...auditStamp(),
        id,
        folio,
        personId: actor.id,
        personName: actor.displayName,
        areaId: actor.areaId,
        subAreaId: actor.subAreaId,
        subAreaName: subAreaNameOf(actor.subAreaId),
        kind: input.kind,
        range: input.range,
        // Natural days, frozen at registration: the IMSS subsidises calendar
        // days, so a business count would understate the leave.
        naturalDays: input.range.naturalDays,
        imssFolio: input.imssFolio,
        umf: input.umf,
        subsidy: kind?.subsidy ?? '—',
        coverage: input.coverage,
        certificatePath: input.certificatePath,
        status: 'Por validar',
        notes: input.notes,
        validatedByPersonId: null,
        validatedAt: null,
        observation: null,
      },
      ...demoStore.incapacities,
    ];

    return Promise.resolve(ok(id));
  }

  validate(id: string): Promise<Result<void, RepositoryError>> {
    this.patch(id, { status: 'Validada', validatedAt: new Date(), observation: null });
    return Promise.resolve(ok(undefined));
  }

  observe(id: string, observation: string): Promise<Result<void, RepositoryError>> {
    this.patch(id, { status: 'Observada', observation });
    return Promise.resolve(ok(undefined));
  }

  cancel(id: string, reason: string): Promise<Result<void, RepositoryError>> {
    this.patch(id, { status: 'Cancelada', observation: reason });
    return Promise.resolve(ok(undefined));
  }

  private patch(id: string, changes: Partial<Incapacity>): void {
    const index = demoStore.incapacities.findIndex((i) => i.id === id);
    const current = demoStore.incapacities[index];
    if (current !== undefined) {
      demoStore.incapacities[index] = { ...current, ...changes, updatedAt: new Date() };
    }
  }
}

// ---------------------------------------------------------------------------
// Holidays, balances, audit log, contacts
// ---------------------------------------------------------------------------

export class DemoHolidayRepository implements IHolidayRepository {
  listForYear(year: number): Promise<Result<readonly Holiday[], RepositoryError>> {
    return Promise.resolve(
      ok(demoStore.holidays.filter((h) => h.year === year).sort((a, b) => a.date.compare(b.date))),
    );
  }

  nonWorkingDates(years: readonly number[]): Promise<Result<ReadonlySet<string>, RepositoryError>> {
    return Promise.resolve(
      ok(enabledHolidayDates(demoStore.holidays.filter((h) => years.includes(h.year)))),
    );
  }

  add(input: {
    date: { toISO: () => string };
    name: string;
    type: 'Obligatorio' | 'Por costumbre';
  }): Promise<Result<HolidayId, RepositoryError>> {
    const id = asId<'HolidayId'>(`knotion_${input.date.toISO()}`);
    return Promise.resolve(ok(id));
  }

  setEnabled(id: HolidayId, enabled: boolean): Promise<Result<void, RepositoryError>> {
    const index = demoStore.holidays.findIndex((h) => h.id === id);
    const current = demoStore.holidays[index];
    if (current !== undefined) demoStore.holidays[index] = { ...current, enabled };
    return Promise.resolve(ok(undefined));
  }

  remove(id: HolidayId): Promise<Result<void, RepositoryError>> {
    demoStore.holidays = demoStore.holidays.filter((h) => h.id !== id);
    return Promise.resolve(ok(undefined));
  }

  seedYear(): Promise<Result<number, RepositoryError>> {
    return Promise.resolve(ok(0));
  }
}

export class DemoBalanceRepository implements IBalanceRepository {
  get(personId: PersonId, year: number): Promise<Result<Balance, RepositoryError>> {
    const found = demoStore.balances.find((b) => b.personId === personId && b.year === year);
    return Promise.resolve(found === undefined ? err(notFound('balance')) : ok(found));
  }

  getMany(refs: readonly BalanceRef[]): Promise<Result<readonly Balance[], RepositoryError>> {
    return Promise.resolve(
      ok(
        demoStore.balances.filter((b) =>
          refs.some((ref) => ref.personId === b.personId && ref.periodYear === b.year),
        ),
      ),
    );
  }

  listAtRisk(
    areaId: AreaId,
    year: number,
    threshold: number,
    page: PageRequest,
  ): Promise<Result<Page<Balance>, RepositoryError>> {
    const items = demoStore.balances
      .filter((b) => b.areaId === areaId && b.year === year && b.remainingDays > threshold)
      .sort((a, b) => b.remainingDays - a.remainingDays);
    return Promise.resolve(ok(paged(items, page)));
  }
}

export class DemoAuditLogRepository implements IAuditLogRepository {
  list(scope: AuditScope, page: PageRequest): Promise<Result<Page<AuditEntry>, RepositoryError>> {
    return Promise.resolve(ok(paged(this.filter(scope), page)));
  }

  count(scope: AuditScope): Promise<Result<number, RepositoryError>> {
    return Promise.resolve(ok(this.filter(scope).length));
  }

  private filter(scope: AuditScope): AuditEntry[] {
    return demoStore.auditEntries
      .filter((e) => e.areaId === scope.areaId)
      .filter((e) => scope.kind === undefined || e.kind === scope.kind)
      .filter((e) => scope.actorPersonId === undefined || e.actorPersonId === scope.actorPersonId)
      .filter((e) => {
        if (scope.from === undefined) return true;
        return e.createdAt.getTime() >= Date.parse(`${scope.from.toISO()}T00:00:00Z`);
      })
      .filter((e) => {
        if (scope.to === undefined) return true;
        return e.createdAt.getTime() <= Date.parse(`${scope.to.toISO()}T23:59:59Z`);
      })
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  getById(id: string): Promise<Result<AuditEntry, RepositoryError>> {
    const found = demoStore.auditEntries.find((e) => e.id === id);
    return Promise.resolve(found === undefined ? err(notFound('audit entry')) : ok(found));
  }
}

/** Emergency contacts — read and written locally. */
/*
 * `implements` is not decoration here.
 *
 * Without it this class drifted from the port — `create` returned a bare `string`
 * where the interface promised an `EmergencyContactId` — and the drift was
 * invisible because the composition root typed the field as this class rather
 * than as the interface. That is how the fake ended up serving production. Both
 * ends are now nailed to the port.
 */
export class DemoEmergencyContactRepository implements IEmergencyContactRepository {
  listFor(personId: PersonId): Promise<Result<readonly EmergencyContact[], RepositoryError>> {
    return Promise.resolve(
      ok(
        demoStore.contacts
          .filter((c) => c.personId === personId)
          .sort((a, b) => a.preferenceOrder - b.preferenceOrder),
      ),
    );
  }

  create(
    input: CreateEmergencyContactInput,
  ): Promise<Result<EmergencyContactId, RepositoryError>> {
    const id = `ec-${String(demoStore.contacts.length + 1)}`;
    demoStore.contacts.push({
      id: asId<'EmergencyContactId'>(id),
      personId: input.personId,
      areaId: input.areaId,
      name: input.name,
      relation: input.relation,
      phone: input.phone,
      preferenceOrder: demoStore.contacts.filter((c) => c.personId === input.personId).length,
      createdAt: new Date(),
      updatedAt: new Date(),
      schemaVersion: 1,
    });
    return Promise.resolve(ok(asId<'EmergencyContactId'>(id)));
  }

  update(
    contactId: EmergencyContactId,
    patch: UpdateEmergencyContactInput,
  ): Promise<Result<void, RepositoryError>> {
    const index = demoStore.contacts.findIndex((c) => c.id === contactId);
    const current = demoStore.contacts[index];
    if (current !== undefined) {
      demoStore.contacts[index] = { ...current, ...patch, updatedAt: new Date() };
    }
    return Promise.resolve(ok(undefined));
  }

  delete(contactId: EmergencyContactId): Promise<Result<void, RepositoryError>> {
    demoStore.contacts = demoStore.contacts.filter((c) => c.id !== contactId);
    return Promise.resolve(ok(undefined));
  }

  reorder(
    personId: PersonId,
    orderedIds: readonly EmergencyContactId[],
  ): Promise<Result<void, RepositoryError>> {
    orderedIds.forEach((id, order) => {
      const index = demoStore.contacts.findIndex((c) => c.id === id && c.personId === personId);
      const current = demoStore.contacts[index];
      if (current !== undefined) {
        demoStore.contacts[index] = { ...current, preferenceOrder: order };
      }
    });
    return Promise.resolve(ok(undefined));
  }
}

// ---------------------------------------------------------------------------
// Organigrama
// ---------------------------------------------------------------------------

/**
 * El organigrama en modo demo, guardado en el navegador.
 *
 * Es lo único del demo que sobrevive a recargar la página, y a propósito: el
 * organigrama se prueba en local antes de tener servidor, y la prueba que importa
 * —«¿se guardó?»— es recargar y ver que sigue ahí. Si el navegador no deja
 * guardar, se queda en memoria y funciona igual hasta recargar.
 *
 * Comprueba el permiso como lo hará la callable: un demo que dejara guardar a
 * cualquiera escondería justo la regla que se pidió, que armarlo se da persona
 * por persona.
 */
export class DemoOrgChartRepository implements IOrgChartRepository {
  private readonly memory = new Map<string, unknown>();

  private storageKey(areaId: AreaId): string {
    return `knpeople.demo.organigrama.${areaId}`;
  }

  get(areaId: AreaId): Promise<Result<OrgChart | null, RepositoryError>> {
    let raw: unknown = this.memory.get(areaId);
    try {
      const stored = window.localStorage.getItem(this.storageKey(areaId));
      if (stored !== null) raw = JSON.parse(stored);
    } catch {
      /* Sin almacenamiento del navegador, o con algo ilegible: vale lo de memoria. */
    }
    if (raw === undefined) return Promise.resolve(ok(null));
    return Promise.resolve(ok(decodeOrgChart(areaId, raw)));
  }

  save(input: SaveOrgChartInput): Promise<Result<OrgChart, RepositoryError>> {
    const actor = demoStore.actor();
    const puede =
      actor !== undefined &&
      personCan(demoStore.config.rolePermissions, actor.role, 'editar_organigrama', {
        extra: actor.extraPermissions.filter(isPermission),
        revoked: actor.revokedPermissions.filter(isPermission),
      });
    if (!puede) {
      return Promise.resolve(err({
          kind: 'permission-denied',
          what: 'Armar el organigrama se da persona por persona, y tu cuenta no lo tiene.',
        }));
    }
    const cruzaAreas = actor.role === 'global' || actor.role === 'super';
    if (!cruzaAreas && actor.areaId !== input.areaId) {
      return Promise.resolve(err({
          kind: 'permission-denied',
          what: 'Sólo puedes armar el organigrama de tu propia área.',
        }));
    }
    if (input.body.nodes.length > ORG_CHART_MAX_NODES) {
      return Promise.resolve(
        err({
          kind: 'invalid',
          what: `Caben ${String(ORG_CHART_MAX_NODES)} tarjetas como máximo.`,
        }),
      );
    }

    const record = {
      nodes: input.body.nodes,
      links: input.body.links,
      updatedAt: new Date().toISOString(),
      updatedByName: actor.displayName,
    };
    this.memory.set(input.areaId, record);
    try {
      window.localStorage.setItem(this.storageKey(input.areaId), JSON.stringify(record));
    } catch {
      /* Queda en memoria: se pierde al recargar, y nada más. */
    }

    const chart = decodeOrgChart(input.areaId, record);
    return Promise.resolve(
      chart === null
        ? err({ kind: 'invalid', what: 'El dibujo no se pudo leer después de guardarlo.' })
        : ok(chart),
    );
  }
}

/**
 * Los cumpleaños del demo, con la misma forma y el mismo candado que
 * `birthdayMonths`: con Kn·Blog apagado no contesta, y nunca manda el año.
 */
export class DemoBirthdayRepository implements IBirthdayRepository {
  listMonth(month: number): Promise<Result<readonly Birthday[], RepositoryError>> {
    if (!demoStore.config.features.knblog) {
      return Promise.resolve(
        err({ kind: 'unavailable', detail: 'El módulo "knblog" está desactivado.' }),
      );
    }
    const areaNames = new Map(demoStore.areas.map((area) => [area.id, area.name]));
    const birthdays = demoStore.people.flatMap((person): Birthday[] => {
      if (person.status !== 'active' || person.birthDate === null) return [];
      if (person.birthDate.month !== month) return [];
      return [
        {
          personId: person.id,
          displayName: person.displayName,
          firstName: person.displayName.split(/\s+/)[0] ?? person.displayName,
          areaId: person.areaId,
          areaName: areaNames.get(person.areaId) ?? '',
          photoURL: person.photoURL,
          month: person.birthDate.month,
          day: person.birthDate.day,
        },
      ];
    });
    return Promise.resolve(ok(birthdays));
  }
}

/** Diez minutos, lo mismo que dura un código de verdad. Ver `LINK_CODE_TTL_MS` en el servidor. */
const DEMO_LINK_CODE_TTL_MS = 10 * 60 * 1000;

/**
 * El vínculo de WhatsApp del demo: enseña el código y **no vincula nada**.
 *
 * No hay asistente detrás al que mandarle el código, y fingir que el número
 * quedó vinculado sería la clase de mentira que ya costó con el botón «Enviar
 * prueba» de los correos. Sirve para revisar cómo se ve la tarjeta, y por eso
 * dice sin número: con uno inventado, el enlace abriría un chat con un
 * desconocido. Igual que la callable, no contesta con el módulo apagado.
 */
export class DemoWhatsAppLinkRepository implements IWhatsAppLinkRepository {
  status(): Promise<Result<WhatsAppLinkStatus, RepositoryError>> {
    return Promise.resolve(ok({ linked: false, numberTail: null, linkedAt: null }));
  }

  start(): Promise<Result<WhatsAppLinkCode, RepositoryError>> {
    if (!demoStore.config.features.whatsapp) {
      return Promise.resolve(
        err({ kind: 'unavailable', detail: 'El módulo "whatsapp" está desactivado.' }),
      );
    }
    return Promise.resolve(
      ok({
        code: 'KN-DEMO42',
        expiresAt: new Date(Date.now() + DEMO_LINK_CODE_TTL_MS),
        botNumber: null,
        deepLink: null,
      }),
    );
  }

  unlink(): Promise<Result<void, RepositoryError>> {
    return Promise.resolve(ok(undefined));
  }
}

/**
 * La configuración del asistente en el demo: guarda en memoria **en qué termina**
 * cada llave y nada más, y con las mismas reglas que el servidor —sólo el
 * superusuario, y un proveedor no se elige sin su llave—. Un demo que dejara
 * elegir OpenAI sin llave enseñaría una pantalla que el servidor no va a permitir.
 *
 * «Probar» dice la verdad: aquí no hay ningún modelo detrás.
 */
export class DemoAssistantSettingsRepository implements IAssistantSettingsRepository {
  private provider: AssistantProvider = 'none';
  private readonly keys: Record<ModelProvider, AssistantKeyStatus | null> = { openai: null, anthropic: null };

  private denied(): RepositoryError | null {
    const actor = demoStore.actor();
    const puede =
      actor !== undefined &&
      actor.role === 'super' &&
      personCan(demoStore.config.rolePermissions, actor.role, 'config', {
        extra: actor.extraPermissions.filter(isPermission),
        revoked: actor.revokedPermissions.filter(isPermission),
      });
    return puede ? null : { kind: 'permission-denied', what: 'Sólo el superusuario configura el asistente.' };
  }

  private view(): AssistantSettings {
    return {
      provider: this.provider,
      keys: { ...this.keys },
      models: { openai: 'gpt-6-luna', anthropic: 'claude-opus-5-5' },
      canStoreKeys: true,
      whatsapp: { connected: false, numberTail: null, dryRun: false },
    };
  }

  get(): Promise<Result<AssistantSettings, RepositoryError>> {
    const denied = this.denied();
    return Promise.resolve(denied === null ? ok(this.view()) : err(denied));
  }

  setProvider(provider: AssistantProvider): Promise<Result<AssistantSettings, RepositoryError>> {
    const denied = this.denied();
    if (denied !== null) return Promise.resolve(err(denied));
    if (provider !== 'none' && this.keys[provider] === null) {
      return Promise.resolve(
        err({ kind: 'unavailable', detail: 'Falta la llave de ese proveedor. Guárdala antes de elegirlo.' }),
      );
    }
    this.provider = provider;
    return Promise.resolve(ok(this.view()));
  }

  setKey(provider: ModelProvider, key: string | null): Promise<Result<AssistantSettings, RepositoryError>> {
    const denied = this.denied();
    if (denied !== null) return Promise.resolve(err(denied));
    if (key === null && this.provider === provider) {
      return Promise.resolve(
        err({ kind: 'unavailable', detail: 'Ese proveedor es el que está en uso. Cambia primero a otro.' }),
      );
    }
    this.keys[provider] =
      key === null
        ? null
        : {
            tail: key.trim().slice(-4),
            updatedAt: new Date(),
            updatedByName: demoStore.actor()?.displayName ?? '',
            readable: true,
          };
    return Promise.resolve(ok(this.view()));
  }

  test(): Promise<Result<AssistantModelTest, RepositoryError>> {
    return Promise.resolve(
      ok({ ok: false, detail: 'En modo demo no hay ningún modelo al que preguntarle: esto no prueba la llave.' }),
    );
  }
}
